import { Dialog } from "primereact/dialog";
import { Messages } from "primereact/messages";
import { ReactNode, useEffect, useRef, useState } from "react";
import { PrimeIcons } from "primereact/api";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Password } from "primereact/password";
import { MultiSelect } from "primereact/multiselect";
import { Dropdown } from "primereact/dropdown";
import { FormFieldTypes } from "../app_utils/constants/FormFieldTypes";
import { getFormFieldComponent } from "../app_utils/components/FormFieldTemplates";
import { formatString } from "../app_utils/utils/Utils";
import { MISSING_FORM_INPUT_MESSAGE } from "../app_utils/constants/ErrorMessages";
import { CSS_COL_12, CSS_COL_6 } from "../app_utils/constants/Constants";
import { BaseApiServiceImpl } from "../app_utils/api/BaseApiServiceImpl";
import { MessageUtils } from "../app_utils/utils/MessageUtils";
import { UserSessionUtils } from "../app_utils/utils/UserSessionUtils";
import * as labels from "../app_utils/constants/Labels";

interface ModalType {
  children?: ReactNode;
  messageRef?: any;
  record: any;
  reloadFn: any;
  isOpen: boolean;
  toggle: () => void;
}

interface RecordType {
  username: string;
  firstName: string;
  lastName: string;
  emailAddress: string;
  phoneNumber: string;
  initialPassword: string;
  roleIds: number[];
  parentUserId?: number;
}

const SubAccountFormDialog = (props: ModalType) => {
  const [record, setRecord] = useState<RecordType | null>(props?.record || null);
  const [roles, setRoles] = useState<any[]>([]);
  const [parentAccounts, setParentAccounts] = useState<any[]>([]);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const message = useRef<any>();

  const isSuperAdmin = UserSessionUtils.isSuperAdmin();
  const isEdit = !!props?.record;

  useEffect(() => {
    setRecord(
      props?.record
        ? {
            username: props.record.username || "",
            firstName: props.record.firstName || "",
            lastName: props.record.lastName || "",
            emailAddress: props.record.emailAddress || "",
            phoneNumber: props.record.phoneNumber || "",
            initialPassword: "",
            roleIds: props.record.roleIds || [],
          }
        : {
            username: "",
            firstName: "",
            lastName: "",
            emailAddress: "",
            phoneNumber: "",
            initialPassword: "",
            roleIds: [],
          }
    );
    fetchRolesFromServer();
    if (isSuperAdmin && !props?.record) {
      fetchParentAccountsFromServer();
    }
  }, [props?.record]);

  const fetchRolesFromServer = () => {
    new BaseApiServiceImpl("/api/v1/users/roles")
      .getRequestWithJsonResponse({ offset: 0, limit: 100 })
      .then(async (response) => {
        setRoles(response?.records || []);
      })
      .catch((error) => {
        MessageUtils.showErrorMessage(message, error.message);
      });
  };

  const fetchParentAccountsFromServer = () => {
    new BaseApiServiceImpl("/api/v1/users")
      .getRequestWithJsonResponse({ offset: 0, limit: 1000 })
      .then(async (response) => {
        const records: any[] = response?.records || [];
        const options = records
          .filter((user: any) => user?.id !== props?.record?.id)
          .map((user: any) => ({
            id: user?.id,
            label: getParentAccountLabel(user),
          }));
        setParentAccounts(options);
      })
      .catch((error) => {
        MessageUtils.showErrorMessage(message, error.message);
      });
  };

  const getParentAccountLabel = (user: any) => {
    if (user?.accountName) return `${user.accountName} (${user.username})`;
    if (user?.firstName && user?.lastName) return `${user.firstName} ${user.lastName} (${user.username})`;
    return user?.username || user?.id;
  };

  const clearForm = () => {
    setRecord(null);
    setRoles([]);
    setParentAccounts([]);
  };

  const validateForm = () => {
    if (!record?.username || !record?.firstName || !record?.lastName || !record?.emailAddress) {
      MessageUtils.showErrorMessage(message, "Please fill in all required fields");
      return false;
    }
    if (!props?.record && !record?.initialPassword) {
      MessageUtils.showErrorMessage(message, "Initial password is required for new sub-accounts");
      return false;
    }
    return true;
  };

  const saveUser = () => {
    if (!validateForm()) return;
    setIsSaving(true);
    const url = props?.record
      ? "/api/v1/users/sub-accounts/" + props.record.id
      : "/api/v1/users/sub-accounts";
    const method = props?.record ? "putRequestWithJsonResponse" : "postRequestWithJsonResponse";

    const payload = {
      username: record?.username,
      firstName: record?.firstName,
      lastName: record?.lastName,
      emailAddress: record?.emailAddress,
      phoneNumber: record?.phoneNumber,
      initialPassword: record?.initialPassword,
      roleIds: record?.roleIds,
      ...(record?.parentUserId ? { parentUserId: record?.parentUserId } : {}),
    };

    new BaseApiServiceImpl(url)
      [method](payload)
      .then(async (response) => {
        setIsSaving(false);
        MessageUtils.showSuccessMessage(
          props?.messageRef,
          labels.LABEL_RECORD_SAVED_SUCCESSFULLY
        );
        closeDialog();
        props?.reloadFn();
      })
      .catch((error) => {
        setIsSaving(false);
        MessageUtils.showErrorMessage(message, error.message);
      });
  };

  const closeDialog = () => {
    clearForm();
    props.toggle();
  };

  const userDetailsDialogFooter = (
    <>
      <Button
        label={labels.LABEL_CANCEL}
        icon={PrimeIcons.TIMES}
        className="p-button-text"
        onClick={closeDialog}
      />
      <Button
        label={labels.LABEL_SAVE}
        icon={PrimeIcons.SAVE}
        className="p-button-secondary"
        onClick={saveUser}
        loading={isSaving}
      />
    </>
  );

  return (
    <Dialog
      visible={props.isOpen}
      header={isEdit ? "Edit Sub Account" : "Create Sub Account"}
      footer={userDetailsDialogFooter}
      modal
      className="p-fluid"
      onHide={closeDialog}
      style={{ width: "50vw" }}
    >
      <div className="grid">
        <div className="col-12">
          <Messages ref={message} style={{ width: "100%" }} />
        </div>
        <div className="col-12 md:col-6">
          <label htmlFor="firstName">First Name</label>
          <InputText
            id="firstName"
            value={record?.firstName || ""}
            onChange={(e) => setRecord((prevState: any) => ({ ...prevState, firstName: e.target.value }))}
            className="w-full mt-1"
          />
        </div>
        <div className="col-12 md:col-6">
          <label htmlFor="lastName">Last Name</label>
          <InputText
            id="lastName"
            value={record?.lastName || ""}
            onChange={(e) => setRecord((prevState: any) => ({ ...prevState, lastName: e.target.value }))}
            className="w-full mt-1"
          />
        </div>
        <div className="col-12">
          <label htmlFor="username">Username</label>
          <InputText
            id="username"
            value={record?.username || ""}
            onChange={(e) => setRecord((prevState: any) => ({ ...prevState, username: e.target.value }))}
            className="w-full mt-1"
            disabled={isEdit}
          />
        </div>
        <div className="col-12">
          <label htmlFor="emailAddress">Email Address</label>
          <InputText
            id="emailAddress"
            value={record?.emailAddress || ""}
            onChange={(e) => setRecord((prevState: any) => ({ ...prevState, emailAddress: e.target.value }))}
            className="w-full mt-1"
          />
        </div>
        <div className="col-12">
          <label htmlFor="phoneNumber">Phone Number</label>
          <InputText
            id="phoneNumber"
            value={record?.phoneNumber || ""}
            onChange={(e) => setRecord((prevState: any) => ({ ...prevState, phoneNumber: e.target.value }))}
            className="w-full mt-1"
          />
        </div>
        <div className="col-12">
          <label htmlFor="roleIds">Roles</label>
          <MultiSelect
            id="roleIds"
            value={record?.roleIds || []}
            options={roles}
            optionValue="id"
            optionLabel="name"
            onChange={(e) => setRecord((prevState: any) => ({ ...prevState, roleIds: e.value }))}
            className="w-full mt-1"
            display="chip"
            placeholder="Select Roles"
          />
        </div>
        {isSuperAdmin && !isEdit && (
          <div className="col-12">
            <label htmlFor="parentUserId">Parent Account (Company)</label>
            <Dropdown
              id="parentUserId"
              value={record?.parentUserId}
              options={parentAccounts}
              optionValue="id"
              optionLabel="label"
              onChange={(e) => setRecord((prevState: any) => ({ ...prevState, parentUserId: e.value }))}
              className="w-full mt-1"
              filter
              showClear
              placeholder="Assign to a company (sub-account uses its credits)"
            />
            <small className="p-text-secondary">
              Leave blank to make this a standalone account.
            </small>
          </div>
        )}
        {!isEdit && (
          <div className="col-12">
            <label htmlFor="initialPassword">Initial Password</label>
            <Password
              id="initialPassword"
              value={record?.initialPassword || ""}
              onChange={(e) => setRecord((prevState: any) => ({ ...prevState, initialPassword: e.target.value }))}
              className="w-full mt-1"
              feedback={true}
              toggleMask={true}
            />
          </div>
        )}
      </div>
    </Dialog>
  );
};

export default SubAccountFormDialog;
